import zIndex from '@material-ui/core/styles/zIndex'
import React from 'react'
import ReactSelect, { Props as ReactSelectProps, components } from 'react-select'

const defaultStyles = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: 10,
    border: '1px solid var(--color-ghost-grey)',
    padding: '4px 12px',
    minHeight: 45,
    boxShadow: state.isFocused ? '0 0 0 1px var(--color-hawkes-blue)' : 'none',
    '&:hover': {
      border: '1px solid var(--color-ghost-grey)',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none', // Hide the separator
  }),
  dropdownIndicator: provided => ({
    ...provided,
    color: 'var(--color-text-form-label)', // Change dropdown icon color
    '&:hover': {
      color: 'var(--color-text-form-label)',
    },
  }),
  menu: provided => ({
    ...provided,
    borderRadius: 10,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 10000,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'var(--color-hawkes-blue)' : 'white',
    color: 'var(--color-text-primary)',
    '&:hover': {
      backgroundColor: 'var(--color-hawkes-blue)',
    },
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: 'var(--color-hawkes-blue)',
    borderRadius: 8,
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: 'var(--color-text-secondary)',
    fontSize: '14px',
    fontWeight: 500,
  }),
  multiValueRemove: provided => ({
    ...provided,
    color: 'var(--color-text-secondary)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
      color: 'var(--color-text-secondary)',
    },
  }),
  placeholder: (provided, state) => ({
    ...provided,
    color: 'var(--color-text-placeholder)',
    fontSize: '14px',
  }),
}

interface SelectProps extends Omit<ReactSelectProps, 'error'> {
  label?: string
  required?: boolean
  error?: boolean | string
  errorMessage?: string
  maxDisplayValues?: number
}

const Select: React.FC<SelectProps> = ({
  styles = {},
  label,
  required,
  error,
  errorMessage,
  maxDisplayValues = 4,
  classNamePrefix = 'cb-select',
  ...props
}) => {
  // Merge styles and override control border based on error state
  const mergedStylesBase: any = {
    ...defaultStyles,
    ...styles,
  }

  const mergedStyles: any = {
    ...mergedStylesBase,
    control: (provided, state) => {
      const baseControl = defaultStyles.control(provided, state)
      const userControl = typeof styles?.control === 'function' ? styles.control(provided, state) : {}
      const errorBorder = '1px solid var(--color-text-error)'
      const normalBorder = '1px solid var(--color-ghost-grey)'

      return {
        ...baseControl,
        ...userControl,
        border: error ? errorBorder : normalBorder,
        boxShadow: state.isFocused
          ? error
            ? '0 0 0 1px var(--color-text-error)'
            : '0 0 0 1px var(--color-hawkes-blue)'
          : 'none',
        '&:hover': {
          ...(baseControl['&:hover'] || {}),
          ...(userControl['&:hover'] || {}),
          border: error ? errorBorder : normalBorder,
        },
      }
    },
  }

  return (
    <div style={{ width: '100%' }}>
      {label && (
        <div
          style={{
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '16px',
            color: 'var(--color-text-form-label)',
            marginBottom: '10px',
          }}>
          {label}
          {required && <span style={{ color: 'var(--color-text-error)', marginLeft: '2px' }}>*</span>}
        </div>
      )}
      <ReactSelect
        styles={mergedStyles}
        classNamePrefix={classNamePrefix}
        components={{
          ClearIndicator: () => null, // Remove the clear all icon
          MultiValueContainer: ({ children, data, selectProps, ...rest }) => {
            const { value } = selectProps
            const values = Array.isArray(value) ? value : []
            const index = values.findIndex(val => val.value === data.value)

            // Only render the first maxDisplayValues items
            if (index < maxDisplayValues) {
              return <components.MultiValueContainer {...rest}>{children}</components.MultiValueContainer>
            }

            // For the last visible item, show how many more are selected
            if (index === maxDisplayValues) {
              const remaining = values.length - maxDisplayValues
              return (
                <components.MultiValueContainer {...rest}>
                  <div
                    style={{
                      padding: '3px 6px 3px 6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'var(--color-text-secondary)',
                      borderRadius: 2,
                    }}>
                    + {remaining} more
                  </div>
                </components.MultiValueContainer>
              )
            }

            // Don't render items beyond maxDisplayValues + 1
            return null
          },
        }}
        {...props}
      />
      {error && errorMessage && (
        <p
          style={{
            fontSize: '12px',
            position: 'absolute',
            color: 'var(--color-text-error)',
            margin: '0 0',
          }}>
          {errorMessage}
        </p>
      )}
    </div>
  )
}

export default Select
